/* eslint-disable @typescript-eslint/no-empty-object-type */
import { GraphQLResponse } from "graphql-request";
import {
  DEFAULT_LOCALE,
  LOCALE_API_MAP,
  mapLocaleToApiFormat,
} from "@/i18n/routing";
import { IMAGE_FRAGMENT, SEO_PROPERTY_FRAGMENT } from "@/queries/fragments";
import { getPublicEnv } from '@/utils/env';

const API_URL = getPublicEnv('NEXT_PUBLIC_STRAPI_GRAPHQL_URL') || "";

/**
 *
 * @param query graphql query string
 * @param variables variables for the query
 * @param variables.locale locale for the query
 * @param currentLocale current locale that is being used by browser
 * @returns
 */

export type Variables = Record<
  string,
  string | number | boolean | null | undefined
>;

/**
 * Ensure `locale` is always included in variables.
 */
export type VariablesWithLocale<T extends Variables = Record<string, never>> =
  T & { locale: string };
export async function fetchCMS<TData, TVariables extends Variables = {}>(
  query: string,
  variables?: TVariables,
  targetLocale: string = DEFAULT_LOCALE,
): Promise<TData> {
  const locale = mapLocaleToApiFormat(targetLocale) || DEFAULT_LOCALE;
  const vars = { ...variables, locale };
  const requestHeaders: HeadersInit = {
    "Content-Type": "application/json",
    "X-Target-Language": LOCALE_API_MAP[locale],
  };

  // Track query execution time for debugging
  const startTime = Date.now();

  // Execute GraphQL query request
  const res = await fetch(API_URL, {
    method: "POST",
    headers: requestHeaders,
    body: JSON.stringify({ query, variables: vars }),
    next: { revalidate: 5 * 60 },
  });

  // Extract readable query name (for log clarity)
  const queryDebugText = query
    ?.substring(0, query?.indexOf("("))
    ?.replace(IMAGE_FRAGMENT, "")
    ?.replace(SEO_PROPERTY_FRAGMENT, "")
    ?.trim();
  const queryTime = Date.now() - startTime;
  if (queryTime >= 10000) {
    console.log(
      `debug query time of ${queryDebugText} client `,
      queryTime + "ms",
    );
  }

  // Log slow queries (10s+ execution time)
  if (!res.ok) {
    console.log("GRAPH ERROR !RES.OK client", JSON.stringify(res));
    console.log("GRAPH ERROR QUERY !RES.OK", JSON.stringify({ query }));
    console.log("GRAPH ERROR QUERY !RES.OK", JSON.stringify(variables));
  }

  const { data, errors }: GraphQLResponse<TData> = await res.json();

  // Log GraphQL-level errors (do not throw by default to avoid client crashes)
  if (errors?.length) {
    console.log("GRAPH ERROR ERRORS.LENGTH", JSON.stringify(errors));
    console.log("GRAPH ERROR QUERY ERRORS.LENGTH", JSON.stringify({ query }));
    console.log("GRAPH ERROR QUERY ERRORS.LENGTH", JSON.stringify(variables));
  }

  // Ensure data was received; throw if missing
  if (!data) {
    console.log("GRAPH ERROR !DATA", JSON.stringify(res));
    console.log("GRAPH ERROR QUERY !DATA", JSON.stringify({ query }));
    console.log("GRAPH ERROR QUERY !DATA", JSON.stringify(variables));
    throw new Error("Received empty response from GraphQL endpoint");
  }

  return data;
}
